import { Tooltip } from 'antd'
import axios from 'axios'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { clickTrigger } from '../../utils/layoutConst'
import { requestGeolocationApiPath } from '../../utils/paths'

export interface IpGeolocationTooltipProps {
  ipAddress?: string
  children?: React.ReactElement | string
}

interface IpGeolocationData {
  status: 'success' | 'fail'
  message?: string
  regionName?: string
  city?: string
  isp?: string
  mobile?: boolean
  proxy?: boolean
}

const errorMessage = (
  <div className="px-2 py-8 w-36 text-center text-red-300">
    Nepodařilo se lokalizovat IP adresu
  </div>
)

export const IpGeolocationTooltip: FunctionComponent<IpGeolocationTooltipProps> =
  ({ ipAddress, children }) => {
    const [data, setData] = useState<IpGeolocationData | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    const fetchGeolocation = useCallback(async () => {
      if (data || loading) return

      setLoading(true)
      setError(false)

      try {
        const response = await axios.get<IpGeolocationData>(
          requestGeolocationApiPath(ipAddress)
        )
        setData(response.data)
      } catch (err) {
        setError(true)
      } finally {
        setLoading(false)
      }
    }, [data, ipAddress, loading])

    const handleOpenChange = useCallback(
      (visible: boolean) => {
        if (visible) {
          fetchGeolocation()
        }
      },
      [fetchGeolocation]
    )

    const renderTooltipContent = useMemo(() => {
      if (error) return errorMessage
      if (loading)
        return (
          <div className="py-8 w-36 text-center animate-pulse">Načítání...</div>
        )
      if (!data) return <div className="p-2">Klikněte pro načtení dat</div>
      if (data.status === 'fail') return errorMessage

      return (
        <div className="p-2 grid grid-cols-[90px_140px] gap-x-3 gap-y-1">
          <>
            <strong className="text-gray-400">Geolokace IP:</strong>
            <span className="text-gray-400">{ipAddress}</span>
          </>
          <>
            <strong>Město:</strong>
            {data.city ? (
              <span>{data.city}</span>
            ) : (
              <span className="text-gray-500">nezjištěno</span>
            )}
          </>

          {data.regionName && (
            <>
              <strong>Region:</strong>
              <span>{data.regionName}</span>
            </>
          )}
          {data.isp && (
            <>
              <strong>Poskytovatel:</strong>
              <span>{data.isp}</span>
            </>
          )}
          <>
            <strong>Mobilní:</strong>
            <span>{data.mobile ? 'Ano' : 'Ne'}</span>
          </>
          {data.proxy && (
            <>
              <strong>Proxy / VPN:</strong>
              <span>{data.proxy ? 'Ano' : 'Ne'}</span>
            </>
          )}
        </div>
      )
    }, [loading, error, data, ipAddress])

    if (!ipAddress || !children) return null

    return (
      <Tooltip
        title={renderTooltipContent}
        onOpenChange={handleOpenChange}
        autoAdjustOverflow
        trigger={clickTrigger}
      >
        <div className="pb-0.5 inline border-b border-dashed border-gray-800 cursor-help hover:opacity-70">
          {children}
        </div>
      </Tooltip>
    )
  }
